import { useEffect, useMemo } from 'react';

import { debounce } from 'lodash';
import useSWR, { mutate } from 'swr';

import { apiClient } from '@src/clients';
import { VisualizationDetailModel } from '@src/clients/atlas/api';

const inProgress = new Set<string>();
const refetch = (CACHE_KEY: string) => mutate(CACHE_KEY);
const debouncedRefetch = debounce(refetch, 2000);
let staticInstance = 0;

interface UseGetVisualizationListByAoiId {
  aoi_id: string | null;
}

export const useGetVisualizationListByAoiId = ({
  aoi_id
}: UseGetVisualizationListByAoiId) => {
  const CACHE_KEY = `${aoi_id}/visualizations`;

  const { data: { data: { visualization } = {} } = {}, isLoading } = useSWR(
    aoi_id ? CACHE_KEY : null,
    () =>
      apiClient.atlasApi.v2.listVisualizationsV2AoisAoiIdVisualizationsGet(
        aoi_id!
      ),
    {
      onErrorRetry: error => {
        if (error.status === 404) return;
      },
      onSuccess: data => {
        const visualizations = data.data.visualization;

        for (const visualization of visualizations) {
          if (visualization.progress !== 100) {
            inProgress.add(visualization.id);
            continue;
          }

          inProgress.delete(visualization.id);
        }

        if (inProgress.size > 0) debouncedRefetch(CACHE_KEY);
      }
    }
  );

  useEffect(() => {
    staticInstance++;

    return () => {
      staticInstance--;
      if (staticInstance === 0) debouncedRefetch.cancel();
    };
  }, []);

  return useMemo(
    () => ({
      visualizationList: (visualization ?? []) as VisualizationDetailModel[],
      isVisualizationListByAoiIdLoading: isLoading
    }),
    [visualization, isLoading]
  );
};
